import { useState } from 'react';
import {
  fetchMessages,
  fetchChatMessages,
  isApiError,
  postMessage,
} from 'src/services/client';
import { toast } from 'react-toastify';

interface UseMessagesAPIState {
  messages: any[];
  loadingMessages: boolean;
  isSending: boolean;
}

export interface UseMessagesAPIReturn {
  messages: any[];
  loadingMessages: boolean;
  isSending: boolean;
  fetchMessagesFromAPI: (
    referenceId: string,
    referenceType?: 'Ticket' | 'Suite'
  ) => Promise<void>;
  fetchMessagesWithToken: (referenceId: string) => Promise<void>;
  postMessageToAPI: (messageData: any) => Promise<void>;
  clearMessages: () => void;
}

const sortMessagesNewestFirst = (messages: any[]): any[] => {
  if (!messages) {
    return [];
  }

  return [...messages].sort(
    (a: any, b: any) =>
      new Date(b.sentOn).getTime() - new Date(a.sentOn).getTime()
  );
};

export const useMessagesAPI = (token?: string): UseMessagesAPIReturn => {
  const [state, setState] = useState<UseMessagesAPIState>({
    messages: [],
    loadingMessages: false,
    isSending: false,
  });

  const setLoadingMessages = (loadingMessages: boolean) => {
    setState((prev: UseMessagesAPIState) => ({ ...prev, loadingMessages }));
  };

  const fetchMessagesFromAPI = async (
    referenceId: string,
    referenceType: 'Ticket' | 'Suite' = 'Ticket'
  ): Promise<void> => {
    try {
      setLoadingMessages(true);

      const response: any = await fetchMessages({
        ReferenceId: referenceId,
        ReferenceType: referenceType,
      });

      if (response.ok || response.status === 200) {
        const rawMessages: any[] = await response.json();
        const sortedMessages: any[] = sortMessagesNewestFirst(rawMessages);

        setState((prev: UseMessagesAPIState) => ({
          ...prev,
          messages: sortedMessages,
        }));
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const fetchMessagesWithToken = async (referenceId: string): Promise<void> => {
    try {
      setLoadingMessages(true);

      const response: any = await fetchChatMessages(
        referenceId,
        'Ticket',
        token
      );

      const raw: any[] = isApiError(response)
        ? []
        : await (response as Response).json();

      const flattenedMessages: any[] = (raw ?? []).flatMap(
        (group: any) => group.messages ?? []
      );

      const sortedMessages: any[] = sortMessagesNewestFirst(flattenedMessages);

      setState((prev: UseMessagesAPIState) => ({
        ...prev,
        messages: sortedMessages,
      }));
    } catch (error: any) {
      console.error('Error fetching messages with token:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const postMessageToAPI = async (messageData: any): Promise<void> => {
    try {
      setState((prev: UseMessagesAPIState) => ({ ...prev, isSending: true }));

      const response: any = await postMessage(messageData, token);

      if (!response.ok && response.status !== 200) {
        throw new Error('Network request failed');
      }

      const responseData: any = await response.json();

      if (responseData?.statusCode === 200 && responseData.messages?.id) {
        const newMessage: any = responseData.messages;

        setState((prev: UseMessagesAPIState) => {
          const mergedMessages: any[] = [newMessage, ...prev.messages];
          const sortedMessages: any[] = sortMessagesNewestFirst(mergedMessages);

          return { ...prev, messages: sortedMessages };
        });

        return;
      }

      throw new Error('Invalid response data');
    } catch (error: any) {
      console.error('Error posting message:', error);
      toast.error('🚫 Message sending failed.', {
        autoClose: 3000,
        position: 'top-center',
      });
    } finally {
      setState((prev: UseMessagesAPIState) => ({ ...prev, isSending: false }));
    }
  };

  const clearMessages = () => {
    setState((prev: UseMessagesAPIState) => ({
      ...prev,
      messages: [],
      loadingMessages: false,
      isSending: false,
    }));
  };

  return {
    messages: state.messages,
    loadingMessages: state.loadingMessages,
    isSending: state.isSending,
    fetchMessagesFromAPI,
    fetchMessagesWithToken,
    postMessageToAPI,
    clearMessages,
  };
};
